import React, { useState, useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  useNavigate,
  useLocation,
} from "react-router-dom";
import Header from "./components/Header";
import SeoHead from "./components/SeoHead";
import Landing from "./components/Landing";
import TaxCalculator from "./components/TaxCalculator";
import Affordability from "./components/Affordability";
import BondRepaymentPage from "./components/BondRepaymentPage";
import MonthlyFinances from "./components/MonthlyFinances";
import About from "./components/About";
import NotFound from "./components/NotFound";
import AuthModal from "./components/AuthModal";
import SavedCalculationsModal from "./components/SavedCalculationsModal";
import { useAuth } from "./components/AuthProvider";
import type { CalculationType } from "./utils/api";
import {
  ROUTES,
  legacyHashToPath,
  routeForCalculationType,
  isLandingPath,
  pageTitleForPath,
} from "./routes";
import "./App.css";

declare global {
  interface Window {
    gtag: (...args: any[]) => void;
  }
}

type PendingRestore = {
  type: CalculationType;
  payload: Record<string, unknown>;
  calculationId?: string;
  name?: string;
  createdAt?: string;
} | null;

function OAuthCallback() {
  const navigate = useNavigate();
  useEffect(() => {
    const t = setTimeout(() => {
      navigate(ROUTES.carAffordability, { replace: true });
    }, 1500);
    return () => clearTimeout(t);
  }, [navigate]);

  return (
    <div
      className="App"
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "100vh",
      }}
    >
      <p style={{ color: "var(--color-text-muted)" }}>Completing sign in…</p>
    </div>
  );
}

function AppContent() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authModalView, setAuthModalView] = useState<"signin" | "signup">("signin");
  const [showSavedModal, setShowSavedModal] = useState(false);
  const [pendingAfterAuthCallback, setPendingAfterAuthCallback] = useState<
    (() => void) | null
  >(null);
  const [pendingRestore, setPendingRestore] = useState<PendingRestore>(null);

  // One-time migration from hash URLs (#tax, #affordability, …)
  useEffect(() => {
    const { hash } = window.location;
    if (!hash) return;
    const path = legacyHashToPath(hash);
    if (path) {
      navigate(path, { replace: true });
    }
  }, [navigate]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const handleRestoreCalculation = (
    type: CalculationType,
    payload: Record<string, unknown>,
    calculationId?: string,
    name?: string,
    createdAt?: string,
  ) => {
    setPendingRestore({ type, payload, calculationId, name, createdAt });
    navigate(routeForCalculationType(type, payload));
  };

  useEffect(() => {
    if (user && showAuthModal) {
      setShowAuthModal(false);
    }
  }, [user, showAuthModal]);

  const openAuth = (view: "signin" | "signup" = "signin") => {
    setAuthModalView(view);
    setShowAuthModal(true);
  };

  const openAuthForSave = (callback: () => void) => {
    if (user) return;
    setPendingAfterAuthCallback(() => callback);
    openAuth("signin");
  };

  const handleAuthSuccess = () => {
    if (pendingAfterAuthCallback) {
      pendingAfterAuthCallback();
      setPendingAfterAuthCallback(null);
    }
    setShowAuthModal(false);
  };

  useEffect(() => {
    const pageName = pageTitleForPath(location.pathname);
    if (window.gtag) {
      window.gtag("event", "page_view", {
        page_title: pageName,
        page_location: window.location.href,
        page_path: location.pathname,
      });
    }
  }, [location.pathname]);

  useEffect(() => {
    document.body.style.overflow = isLandingPath(location.pathname)
      ? "auto"
      : "hidden";
    return () => {
      document.body.style.overflow = "";
    };
  }, [location.pathname]);

  const clearPendingRestore = () => setPendingRestore(null);

  const calculatorProps = {
    onOpenAuthForSave: openAuthForSave,
    onOpenAuth: () => openAuth("signin"),
    pendingRestore,
    onClearPendingRestore: clearPendingRestore,
  };

  return (
    <div className="App">
      <SeoHead />
      <Header
        onNavigate={navigate}
        pathname={location.pathname}
        onOpenAuth={openAuth}
        onOpenSavedModal={() => setShowSavedModal(true)}
        onRestoreCalculation={handleRestoreCalculation}
      />
      <Routes>
        <Route
          path={ROUTES.home}
          element={
            <Landing
              onNavigate={(path) => navigate(path)}
              onOpenAuth={() => openAuth("signin")}
            />
          }
        />
        <Route
          path={ROUTES.carAffordability}
          element={<Affordability mode="car" {...calculatorProps} />}
        />
        <Route
          path={ROUTES.homeAffordability}
          element={<Affordability mode="home" {...calculatorProps} />}
        />
        <Route
          path={ROUTES.bondRepayment}
          element={<BondRepaymentPage {...calculatorProps} />}
        />
        <Route
          path={ROUTES.monthlyFinances}
          element={<MonthlyFinances {...calculatorProps} />}
        />
        <Route
          path={ROUTES.tax}
          element={
            <TaxCalculator
              onOpenAuthForSave={openAuthForSave}
              onOpenAuth={() => openAuth("signin")}
              pendingRestore={
                pendingRestore?.type === "tax" ? pendingRestore.payload : undefined
              }
              pendingCalculationId={
                pendingRestore?.type === "tax" ? pendingRestore.calculationId : undefined
              }
              pendingName={
                pendingRestore?.type === "tax" ? pendingRestore.name : undefined
              }
              pendingCreatedAt={
                pendingRestore?.type === "tax" ? pendingRestore.createdAt : undefined
              }
              onClearPendingRestore={clearPendingRestore}
            />
          }
        />
        <Route path={ROUTES.about} element={<About />} />
        <Route path={ROUTES.callback} element={<OAuthCallback />} />
        <Route path="*" element={<NotFound />} />
      </Routes>
      {showAuthModal && !user && (
        <AuthModal
          key={authModalView}
          initialView={authModalView}
          onClose={() => {
            setPendingAfterAuthCallback(null);
            setShowAuthModal(false);
          }}
          onSuccess={handleAuthSuccess}
        />
      )}
      <SavedCalculationsModal
        open={showSavedModal}
        onClose={() => setShowSavedModal(false)}
        onRestoreCalculation={handleRestoreCalculation}
      />
    </div>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AppContent />
    </BrowserRouter>
  );
}

export default App;
